import { MoneyDirection } from "@common/constants";
import { TransactionCoaFilterType } from "@common/helpers/transactions";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleConditionAmount,
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    TransactionRule,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { TransactionsFilters } from "@components/transactions/filters/lib";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useRuleEntities } from "@hooks/useRuleEntities";
import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useOverlays } from "contexts/OverlayContext";
import { Button } from "@design-system/Button/Button";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import { ArrowCornerIcon, TriangleIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { TransactionRuleActions } from "../../../transactionRules/TransactionRuleActions";
import { TransactionRuleConditions } from "../../../transactionRules/TransactionRuleConditions";
import styles from "../RulesTable.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HELP_CENTER_URLS, useHelpCenterUrl } from "@hooks/useHelpCenterUrl";

interface TitleSectionProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
}

const TitleSection: React.FC<TitleSectionProps> = ({
    isCollapsed,
    onToggleCollapse,
}) => {
    const rulesHelpCenterUrl = useHelpCenterUrl(HELP_CENTER_URLS.rules);

    return (
        <HStack justify="between">
            <HStack gap={4} align="center">
                <HStack gap={12} align="center">
                    <KickLogo variant="icon" size="xs" />
                    <span>Kick</span>
                </HStack>

                <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    className={classNames(styles.visibleOnHover, {
                        [styles.collapsedCarot]: isCollapsed,
                    })}
                    onClick={onToggleCollapse}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                    aria-expanded={!isCollapsed}
                >
                    <TriangleIcon className="icon-color-gray-500" />
                </Button>
            </HStack>

            {rulesHelpCenterUrl && (
                <Tooltip content="The Kick default Chart of Accounts mapping. Click to read more.">
                    <Button
                        variant="secondary"
                        size="sm"
                        asChild
                        aria-label="Kick default Chart of Accounts mapping"
                    >
                        <a
                            href={rulesHelpCenterUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ArrowCornerIcon />
                        </a>
                    </Button>
                </Tooltip>
            )}
        </HStack>
    );
};

const AccountingRuleEntityCell: React.FC<{ rule: TransactionRule }> = ({
    rule,
}) => {
    const ruleEntities = useRuleEntities(rule);

    return (
        <td className={styles.td}>
            {ruleEntities.length > 0 && (
                <EntityAvatarGroup entities={ruleEntities} max={3} size="xs" />
            )}
        </td>
    );
};

interface AccountingRulesTableProps {
    accountingGroup: TransactionRuleGroup;
    counterparties: Counterparty[];
    normalizedDescriptions: NormalizedDescriptionInfo[];
    priority?: number;
    defaultAccountingTransactionCounts: Record<string, number>;
}

export const AccountingRulesTable: React.FC<AccountingRulesTableProps> = ({
    accountingGroup,
    counterparties,
    normalizedDescriptions,
    priority,
    defaultAccountingTransactionCounts,
}) => {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { openOverlay } = useOverlays();

    const overrideRule = useCallback(
        (rule: TransactionRule) => {
            openOverlay("createRule", {
                definition: {
                    conditions: rule.conditions,
                    actions: rule.actions,
                },
                source: RuleSource.SETTINGS,
                groupType: RuleGroupType.ACCOUNTING,
            });
        },
        [openOverlay],
    );

    const showTransactions = useCallback(
        (rule: TransactionRule) => {
            const entityCondition = rule.conditions.find(
                (condition) =>
                    condition.conditionType === RuleConditionType.ENTITY,
            );

            const categoryCondition = rule.conditions.find(
                (condition) =>
                    condition.conditionType === RuleConditionType.CATEGORY,
            );

            const amountCondition = rule.conditions.find(
                (condition) =>
                    condition.conditionType === RuleConditionType.AMOUNT,
            );

            const filters: TransactionsFilters = {
                entityIds: entityCondition?.value as number[],
                category: categoryCondition?.value as string[],
                coaFilterType: [TransactionCoaFilterType.KICK],
                ...(amountCondition?.value
                    ? {
                          direction:
                              (amountCondition.value as RuleConditionAmount)
                                  .min === 0
                                  ? MoneyDirection.MONEY_IN
                                  : MoneyDirection.MONEY_OUT,
                      }
                    : {}),
            };

            openGlobalTransactionViewDrawer(filters);
        },
        [openGlobalTransactionViewDrawer],
    );

    return (
        <>
            <thead></thead>
            <tbody>
                {!accountingGroup.rules?.length ? (
                    <tr>
                        <td
                            className={classNames(
                                styles.td,
                                styles.groupHeader,
                            )}
                            width={200}
                        >
                            <TitleSection
                                isCollapsed={isCollapsed}
                                onToggleCollapse={() =>
                                    setIsCollapsed(!isCollapsed)
                                }
                            />
                        </td>
                        <td colSpan={4} className={styles.td}>
                            <span>No rules found</span>
                        </td>
                    </tr>
                ) : (
                    accountingGroup.rules?.map((rule, index) =>
                        !isCollapsed || index === 0 ? (
                            <tr key={rule.id} className={styles.rowHover}>
                                {index === 0 && (
                                    <>
                                        <td
                                            className={classNames(
                                                styles.td,
                                                styles.priorityColumn,
                                            )}
                                            rowSpan={
                                                accountingGroup.rules?.length
                                            }
                                        >
                                            {priority}
                                        </td>
                                        <td
                                            className={classNames(
                                                styles.td,
                                                styles.groupHeader,
                                            )}
                                            rowSpan={
                                                accountingGroup.rules?.length
                                            }
                                            width={200}
                                        >
                                            <TitleSection
                                                isCollapsed={isCollapsed}
                                                onToggleCollapse={() =>
                                                    setIsCollapsed(!isCollapsed)
                                                }
                                            />
                                        </td>
                                    </>
                                )}
                                {isCollapsed ? (
                                    <td
                                        colSpan={5}
                                        className={classNames(
                                            styles.td,
                                            styles.rulesCount,
                                        )}
                                    >
                                        {accountingGroup.rules?.length ?? 0}{" "}
                                        Accounting rules
                                    </td>
                                ) : (
                                    <>
                                        <td className={styles.td}>
                                            <TransactionRuleConditions
                                                rule={rule}
                                                counterparties={counterparties}
                                                normalizedDescriptions={
                                                    normalizedDescriptions
                                                }
                                                hideEntityCondition
                                            />
                                        </td>
                                        <td className={styles.td}>
                                            <TransactionRuleActions
                                                rule={rule}
                                                counterparties={counterparties}
                                            />
                                        </td>
                                        <AccountingRuleEntityCell rule={rule} />
                                        <td className={styles.td}>
                                            <div className={styles.ruleRowFlex}>
                                                <Button
                                                    variant="secondary"
                                                    size="md"
                                                    onClick={() =>
                                                        showTransactions(rule)
                                                    }
                                                    className={
                                                        styles.numberOfTransactions
                                                    }
                                                >
                                                    {defaultAccountingTransactionCounts[
                                                        rule.id
                                                    ] ?? 0}
                                                </Button>

                                                <Menu.More
                                                    className={
                                                        styles.managementDropdown
                                                    }
                                                >
                                                    <Menu.Item
                                                        onClick={() => {
                                                            overrideRule(rule);
                                                        }}
                                                    >
                                                        Override
                                                    </Menu.Item>
                                                </Menu.More>
                                            </div>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ) : null,
                    )
                )}
            </tbody>
        </>
    );
};
